import { useMemo } from 'react'

import {
  CostMessagingCoverageModalData,
  CostMessagingModalContent,
  CoverageType,
  SessionsCoverageText,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { GetCostMessagingModalTextParams } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

import { getSessionsCoverageText } from '../customers/util/DefaultCoverageTexts'

interface UseGetCostMessagingInfoProps {
  isCostMessagingEnabled: boolean
  costMessagingModalText?: {
    [key in CoverageType]: (params: GetCostMessagingModalTextParams) => CostMessagingModalContent[]
  }
  coverageModalTitleText?: React.ReactNode
  isHPIExclusive?: boolean
  isEAPExclusive?: boolean
  isDependentDisabled?: boolean
  isCustomerEAPMeds?: boolean
  isCustomerHPIMeds?: boolean
  isCustomerProgramEAPCoaching?: boolean
  isCustomerProgramHPITherapy?: boolean
  isCustomerProgramEAPTherapy?: boolean
  isCustomerNeuroskillsCoachingEnabled?: boolean
  isCustomerCaregiverTrainingEnabled?: boolean
  customerDisplayName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  customerSessionsCoverageText?: SessionsCoverageText
}
/**
 * Hook to retrieve cost messaging information related to a customer's provided coverage
 */
export const useGetCostMessagingInfo = ({
  isCostMessagingEnabled,
  costMessagingModalText,
  coverageModalTitleText,
  isHPIExclusive,
  isEAPExclusive,
  isDependentDisabled,
  isCustomerEAPMeds,
  isCustomerHPIMeds,
  isCustomerProgramEAPCoaching,
  isCustomerProgramHPITherapy,
  isCustomerProgramEAPTherapy,
  isCustomerNeuroskillsCoachingEnabled,
  isCustomerCaregiverTrainingEnabled,
  customerDisplayName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  customerSessionsCoverageText,
}: UseGetCostMessagingInfoProps): CostMessagingCoverageModalData => {
  return useMemo(() => {
    const customerCoverageType = isHPIExclusive
      ? CoverageType.HPI_ONLY
      : isEAPExclusive
      ? CoverageType.EAP_ONLY
      : CoverageType.EAP_AND_HPI

    // Use customer-provided sessionsCoverageText if available, otherwise compute from default logic
    const sessionsCoverageText =
      customerSessionsCoverageText ??
      getSessionsCoverageText({
        coverageType: customerCoverageType,
        isDependentDisabled,
        numberOfSessions: numSessions,
        numberOfSessionsByProgram: numberOfSessionByProgram,
        displayVisitsPerIssuePerYear,
        displayVisitsPerIssuePerYearByProgram,
        isCustomerProgramEAPCoaching,
        isCustomerProgramEAPTherapy,
        isCustomerProgramHPITherapy,
      })

    if (isCostMessagingEnabled) {
      let coverageModalContents: CostMessagingModalContent[] = []

      if (costMessagingModalText) {
        coverageModalContents = costMessagingModalText[customerCoverageType]({
          isDependentDisabled,
          isCustomerEAPMeds,
          isCustomerHPIMeds,
          isCustomerProgramEAPCoaching,
          isCustomerProgramHPITherapy,
          isCustomerProgramEAPTherapy,
          isCustomerNeuroskillsCoachingEnabled,
          isCustomerCaregiverTrainingEnabled,
        })
      }

      // console.debug(
      //   `Cost messaging modal - is supported configuration (coverageModalContents.length > 0): ${
      //     coverageModalContents.length > 0
      //   },
      //   [customerCoverageType: ${customerCoverageType}],
      //   [isDependentDisabled: ${isDependentDisabled}],
      //   [isCustomerEAPMeds: ${isCustomerEAPMeds}],
      //   [isCustomerHPIMeds: ${isCustomerHPIMeds}],
      //   [isCustomerProgramEAPCoaching: ${isCustomerProgramEAPCoaching}],
      //   [isCustomerProgramHPITherapy: ${isCustomerProgramHPITherapy}],
      //   [isCustomerProgramEAPTherapy: ${isCustomerProgramEAPTherapy}],
      //   `,
      // )

      return {
        coverageModalContents,
        coverageModalTitleText,
        isCostMessagingEnabled,
        costMessagingModalCoverageInfo: { customerCoverageType },
        customerDisplayName,
        numSessions,
        numberOfSessionByProgram,
        displayVisitsPerIssuePerYear,
        displayVisitsPerIssuePerYearByProgram,
        sessionsCoverageText,
      }
    }

    return {
      coverageModalContents: [],
      coverageModalTitleText,
      isCostMessagingEnabled,
      costMessagingModalCoverageInfo: undefined,
      customerDisplayName,
      numSessions,
      numberOfSessionByProgram,
      displayVisitsPerIssuePerYear,
      displayVisitsPerIssuePerYearByProgram,
      isDependentDisabled,
      sessionsCoverageText,
    }
  }, [
    isCostMessagingEnabled,
    coverageModalTitleText,
    customerDisplayName,
    numSessions,
    numberOfSessionByProgram,
    displayVisitsPerIssuePerYear,
    displayVisitsPerIssuePerYearByProgram,
    isDependentDisabled,
    isHPIExclusive,
    isEAPExclusive,
    costMessagingModalText,
    isCustomerEAPMeds,
    isCustomerHPIMeds,
    isCustomerProgramEAPCoaching,
    isCustomerProgramHPITherapy,
    isCustomerProgramEAPTherapy,
    isCustomerNeuroskillsCoachingEnabled,
    isCustomerCaregiverTrainingEnabled,
    customerSessionsCoverageText,
  ])
}
